/**
 * annot-layer.js — рисование поверх захвата: СОСТОЯНИЕ и путь пометок в кадр.
 *
 * Один слой на страницу. Он держит пометки, инструмент, стоп-кадр и отдаёт их:
 *   · движку записи — фильтром кадров `layer.recorderFilter` (recorder.js зовёт
 *     его на каждом кадре и на тиках, пока пометки меняются);
 *   · композитору — `layer.paintOver(ctx, W, H)` (лейн MediaRecorder, где
 *     фильтра нет: Firefox, Safari);
 *   · сети — операциями `onOp` (зрители /screen-share рисуют их сами);
 *   · превью — `previewSource()` (последний кадр, ушедший в файл).
 *
 * ⚠️ Главное свойство фильтра — ПРОХОД БЕЗ ПОТЕРЬ, пока рисовать нечего: кадр
 * захвата уходит в кодировщик тем же объектом, без холста. Холст появляется,
 * только пока пометки видны или кадр заморожен: кадры с холста аппаратный
 * H.264 ест медленнее (ScreenRecorderKeepsTheEncoderFedTest, 0.4–7 к/с под
 * нагрузкой GPU), и платить за это должен тот, кто рисует, а не каждый дубль.
 *
 * ⚠️ Метка времени кадра с пометками — метка ИСХОДНОГО кадра захвата. Часы
 * файла в движке строятся по меткам захвата (timeline.stampVideo), поэтому
 * рисование можно включить посреди дубля: движок не видит смены источника.
 *
 * Разбор — docs/why/primitives/screen-annotate.md.
 */
(function () {
    'use strict';
    if (window.ScreenAnnotate) return;
    var C = window.ScreenAnnotCore;

    var COLORS = ['#ff2d55', '#ff9500', '#ffd23f', '#34c759', '#0a84ff', '#ffffff', '#111111'];
    var LIVES = { stay: 0, fade: 3000 };
    var SCENE_EVERY_MS = 300;        // как часто смотрим, не сменился ли кадр под пометками
    var SCENE_CHANGED = 0.3;         // доля клеток, ушедших от кадра пометок
    var SCENE_STEADY = 0.06;         // доля клеток, изменившихся между двумя взглядами
    var SCENE_GRID_W = 32, SCENE_GRID_H = 18;

    function now() { return performance.now(); }
    function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
    function mk(w, h) {
        var c = document.createElement('canvas');
        c.width = Math.max(2, Math.round(w)); c.height = Math.max(2, Math.round(h));
        return c;
    }
    function srcSize(src) {
        if (!src) return null;
        if (typeof src.displayWidth === 'number' && src.displayWidth) return { w: src.displayWidth, h: src.displayHeight };
        if (typeof src.codedWidth === 'number' && src.codedWidth) return { w: src.codedWidth, h: src.codedHeight };
        if (src.tagName === 'VIDEO') return src.videoWidth ? { w: src.videoWidth, h: src.videoHeight } : null;
        if (typeof src.width === 'number' && src.width) return { w: src.width, h: src.height };
        return null;
    }

    function create(opts) {
        opts = opts || {};
        var slug = opts.slug || 'screen-recorder';
        var idPrefix = opts.idPrefix || 'a';
        var S = {
            items: [],
            hist: [],          // {t:'add', it} | {t:'del', its:[{it, at}]} | {t:'clr', its:[...]}
            redo: [],
            seq: 0,
            tool: 'pen',
            color: COLORS[0],
            size: 'm',
            life: 'stay',
            laserIdleAt: undefined,
            ptr: null,         // {x,y,k,c,r}
            spotR: 0.16,
            frozen: null,      // холст стоп-кадра
            freezePending: false,
            dirty: false,
            drawing: null,     // пометка, которую сейчас рисуют
            lastBase: null,    // последний кадр-основа (VideoFrame или холст) — для превью и стоп-кадра
            lastBaseOwned: false,
            baseHasInk: false, // основа уже несёт пометки (композит MediaRecorder-лейна)
            wantPreview: false,
            sceneAuto: true,
            scene: { at: 0, cur: null, prev: null, ink: null, steady: 0, cnv: null },
            stats: { items: 0, tools: {}, freezes: 0, undo: 0, autoClear: 0, saves: 0, recog: 0, frames: 0 },
            listeners: [],
            onOp: typeof opts.onOp === 'function' ? opts.onOp : null,
            scratch: null,
            remote: {},        // указки зрителей /screen-share: id → {x,y,c,n,t}
            zoom: null,        // лупа: {from, to, at} (C.zoomNow)
            zoomScratch: null
        };

        function tel(f, extra) {
            try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: slug, f: f }, extra || {})); } catch (_) {}
        }
        function emit(op) { if (S.onOp) { try { S.onOp(op); } catch (_) {} } }
        function changed() {
            S.dirty = true;
            for (var i = 0; i < S.listeners.length; i++) { try { S.listeners[i](); } catch (_) {} }
            if (S.wake) { try { S.wake(); } catch (_) {} }
        }
        function scratch(w, h) {
            if (!S.scratch) S.scratch = mk(w, h);
            if (S.scratch.width !== w || S.scratch.height !== h) { S.scratch.width = w; S.scratch.height = h; }
            return S.scratch;
        }

        // ── что видно в кадре ────────────────────────────────────────────
        function inkVisible(t) {
            if (S.items.length) {
                C.prune(S.items, t, S.laserIdleAt);
                if (S.items.length) return true;
            }
            return !!(S.ptr);
        }
        function active(t) {
            if (t === undefined) t = now();
            return !!S.frozen || !!C.zoomNow(S.zoom, t) || inkVisible(t);
        }
        // Пометки меняются сами (угасают, указка гаснет, лупа едет) — кадр надо перерисовывать.
        function animating(t) {
            if (C.zoomBusy(S.zoom, t)) return true;
            return S.items.length ? C.prune(S.items, t, S.laserIdleAt) : false;
        }

        // ── основа: кадр, на котором рисуют ──────────────────────────────
        // Последний кадр держится ВСЕГДА, а не только пока открыто превью:
        // «Стоп-кадр» обязан взять то, что было на экране в миг нажатия, а не
        // следующий кадр — за 30 мс экран успевает смениться (стенд
        // screen-recorder-annotate.mjs, D: снят зелёный вместо синего). Клон
        // VideoFrame — ссылка на тот же буфер, копии нет.
        function noteBase(src, owned) {
            if (S.freezePending) takeFreeze(src);
            sceneSample(src);
            dropBase();
            if (owned && src && typeof src.clone === 'function') {
                try { S.lastBase = src.clone(); S.lastBaseOwned = true; } catch (_) { S.lastBase = null; }
            } else {
                S.lastBase = src; S.lastBaseOwned = false;
            }
        }
        function dropBase() {
            if (S.lastBase && S.lastBaseOwned) { try { S.lastBase.close(); } catch (_) {} }
            S.lastBase = null; S.lastBaseOwned = false;
        }

        // ── стоп-кадр ────────────────────────────────────────────────────
        function takeFreeze(src) {
            var dm = srcSize(src);
            if (!dm) return false;
            var c = mk(dm.w, dm.h);
            try { c.getContext('2d').drawImage(src, 0, 0, c.width, c.height); } catch (_) { return false; }
            S.frozen = c;
            S.freezePending = false;
            changed();
            return true;
        }

        // ── «сменился кадр — пометки ушли» ───────────────────────────────
        // Пометка, нарисованная на слайде, после смены слайда указывает в
        // никуда: это жалоба №1 в обзоре рынка (Zoom, Teams — всё остаётся
        // висеть). Сравниваем кадр 32×18 с кадром, на котором рисовали; когда
        // разошлись > 30 % клеток и картинка УСТОЯЛАСЬ (два взгляда подряд
        // почти одинаковы — прокрутка или фильм под пометкой не считаются
        // сменой, пока идут), незакреплённые пометки гаснут.
        function sceneSample(src) {
            if (!S.sceneAuto || S.frozen || S.drawing) return;
            var t = now();
            if (t - S.scene.at < SCENE_EVERY_MS) return;
            S.scene.at = t;
            var hasInk = false;
            for (var i = 0; i < S.items.length; i++) if (S.items[i].k !== 'laser') { hasInk = true; break; }
            if (!hasInk && !S.scene.ink) return;
            var cnv = S.scene.cnv || (S.scene.cnv = mk(SCENE_GRID_W, SCENE_GRID_H));
            var sig;
            try {
                // Без willReadFrequently: уменьшение кадра идёт на GPU, назад
                // читаются 32×18 точек, а не весь кадр захвата.
                var cx = cnv.getContext('2d');
                cx.drawImage(src, 0, 0, SCENE_GRID_W, SCENE_GRID_H);
                var d = cx.getImageData(0, 0, SCENE_GRID_W, SCENE_GRID_H).data;
                sig = new Uint8Array(SCENE_GRID_W * SCENE_GRID_H);
                for (var p = 0, q = 0; q < sig.length; p += 4, q++) sig[q] = (d[p] * 77 + d[p + 1] * 150 + d[p + 2] * 29) >> 8;
            } catch (_) { return; }
            S.scene.prev = S.scene.cur;
            S.scene.cur = sig;
            if (!hasInk) { S.scene.ink = null; return; }
            if (!S.scene.ink) { S.scene.ink = sig; return; }
            var far = diffFrac(sig, S.scene.ink), still = S.scene.prev ? diffFrac(sig, S.scene.prev) : 1;
            if (far > SCENE_CHANGED && still < SCENE_STEADY) {
                S.scene.steady++;
                if (S.scene.steady >= 2) {
                    S.scene.steady = 0;
                    S.scene.ink = null;
                    fadeAll('scene');
                }
            } else S.scene.steady = 0;
        }
        function diffFrac(a, b) {
            var n = 0;
            for (var i = 0; i < a.length; i++) if (Math.abs(a[i] - b[i]) > 24) n++;
            return n / a.length;
        }
        // Пометки на кадре ушли: угасают за FADE_MS, а не исчезают рывком.
        function fadeAll(why) {
            var t = now(), n = 0, gone = [];
            S.items.forEach(function (it) {
                if (it.k === 'laser') return;
                it.done = it.done || t;
                it.life = Math.max(1, t - it.done) + C.FADE_MS;
                gone.push(it.id); n++;
            });
            if (!n) return;
            if (why === 'scene') S.stats.autoClear++;
            emit({ o: 'del', ids: gone });
            changed();
            tel('annot_clear', { why: why, n: n });
        }

        // ── история ──────────────────────────────────────────────────────
        function pushHist(h) { S.hist.push(h); if (S.hist.length > 300) S.hist.shift(); S.redo.length = 0; }
        function idx(id) { for (var i = 0; i < S.items.length; i++) if (S.items[i].id === id) return i; return -1; }

        var api = {
            COLORS: COLORS,

            // ── инструмент ───────────────────────────────────────────────
            setTool: function (k) {
                if (k !== 'eraser' && k !== 'spot' && k !== 'zoom' && C.KINDS.indexOf(k) < 0) return;
                if (S.tool === k) return;
                S.tool = k;
                if (k !== 'laser' && k !== 'spot' && S.ptr) { S.ptr = null; emit({ o: 'ptr' }); changed(); }
                S.stats.tools[k] = (S.stats.tools[k] || 0) + 1;
            },
            tool: function () { return S.tool; },
            setColor: function (c) { if (/^#[0-9a-f]{6}$/i.test(c || '')) S.color = c; },
            color: function () { return S.color; },
            setSize: function (s) { if (C.SIZES[s]) S.size = s; },
            size: function () { return S.size; },
            setLife: function (l) { if (Object.prototype.hasOwnProperty.call(LIVES, l)) S.life = l; },
            life: function () { return S.life; },
            setSceneAuto: function (b) { S.sceneAuto = !!b; S.scene.ink = null; },
            sceneAuto: function () { return S.sceneAuto; },
            spotRadius: function (r) { if (r !== undefined) { S.spotR = clamp(+r || 0.16, 0.05, 0.45); if (S.ptr && S.ptr.k === 'spot') { S.ptr.r = S.spotR; emit({ o: 'ptr', x: S.ptr.x, y: S.ptr.y, k: 'spot', r: S.spotR }); changed(); } } return S.spotR; },

            // ── пометки ──────────────────────────────────────────────────
            // Начать пометку инструментом в точке (доли). Ответ — пометка.
            begin: function (x, y) {
                var k = S.tool;
                if (k === 'eraser' || k === 'spot' || k === 'zoom') return null;
                var it = { id: idPrefix + (++S.seq), k: k, c: S.color, w: C.SIZES[S.size], p: [x, y],
                    life: k === 'laser' ? 0 : LIVES[S.life], done: null };
                if (k === 'line' || k === 'arrow' || k === 'rect' || k === 'oval' || k === 'blur') it.p = [x, y, x, y];
                if (k === 'step') {
                    var n = 0;
                    S.items.forEach(function (o) { if (o.k === 'step' && o.n > n) n = o.n; });
                    it.n = n + 1;
                }
                if (k === 'laser') S.laserIdleAt = now();
                S.items.push(it);
                if (S.items.length > C.MAX_ITEMS) S.items.splice(0, S.items.length - C.MAX_ITEMS);
                S.drawing = (k === 'step' || k === 'text') ? null : it;
                emit({ o: 'add', it: C.wireItem(it) });
                changed();
                return it;
            },
            // Ещё точка штриха / новый угол фигуры.
            extend: function (it, x, y, shift, aspect) {
                if (!it) return;
                if (it.k === 'pen' || it.k === 'hl' || it.k === 'laser') {
                    if (it.p.length / 2 >= C.MAX_POINTS) return;
                    var lx = it.p[it.p.length - 2], ly = it.p[it.p.length - 1];
                    if (Math.abs(lx - x) < 0.0006 && Math.abs(ly - y) < 0.0006) return;
                    it.p.push(x, y);
                    if (it.k === 'laser') S.laserIdleAt = now();
                    emit({ o: 'pts', id: it.id, p: [x, y] });
                } else if (it.p.length >= 4) {
                    it.p = shift ? C.constrain(it.k, it.p[0], it.p[1], x, y, aspect) : [it.p[0], it.p[1], x, y];
                    emit({ o: 'set', id: it.id, p: it.p.slice() });
                }
                changed();
            },
            // Штрих выпрямился (задержали перо): вид и точки меняются на месте.
            reshape: function (it, k, p) {
                if (!it || C.KINDS.indexOf(k) < 0) return;
                it.k = k; it.p = p.slice();
                S.stats.recog++;
                emit({ o: 'set', id: it.id, k: k, p: it.p.slice() });
                changed();
                tel('annot_recognized', { k: k });
            },
            setText: function (it, t) {
                if (!it) return;
                it.t = String(t || '').slice(0, 500);
                emit({ o: 'set', id: it.id, t: it.t });
                changed();
            },
            end: function (it) {
                if (!it) return;
                // Фигура нулевого размера и пустой текст — не пометка.
                var empty = (it.k === 'text' && !String(it.t || '').trim()) ||
                    ((it.k === 'line' || it.k === 'arrow' || it.k === 'rect' || it.k === 'oval' || it.k === 'blur') &&
                        Math.abs(it.p[2] - it.p[0]) < 0.002 && Math.abs(it.p[3] - it.p[1]) < 0.002);
                if (S.drawing === it) S.drawing = null;
                if (empty) {
                    var i0 = idx(it.id);
                    if (i0 >= 0) S.items.splice(i0, 1);
                    emit({ o: 'del', ids: [it.id] });
                    changed();
                    return;
                }
                it.done = now();
                if (it.k === 'laser') S.laserIdleAt = it.done;
                else { pushHist({ t: 'add', it: it }); S.stats.items++; }
                emit({ o: 'end', id: it.id });
                changed();
            },
            // Ластик: убрать пометку под точкой.
            eraseAt: function (x, y, aspect) {
                var i = C.hitTest(S.items, x, y, aspect, 0.012);
                if (i < 0) return false;
                var it = S.items.splice(i, 1)[0];
                pushHist({ t: 'del', its: [{ it: it, at: i }] });
                emit({ o: 'del', ids: [it.id] });
                changed();
                return true;
            },
            clear: function () {
                var its = S.items.filter(function (it) { return it.k !== 'laser'; });
                if (!its.length && !S.items.length) return;
                pushHist({ t: 'clr', its: its.map(function (it) { return { it: it, at: S.items.indexOf(it) }; }) });
                S.items.length = 0;
                S.drawing = null;
                emit({ o: 'clr' });
                changed();
                tel('annot_clear', { why: 'button', n: its.length });
            },
            undo: function () {
                var h = S.hist.pop();
                if (!h) return false;
                S.redo.push(h);
                S.stats.undo++;
                if (h.t === 'add') {
                    var i = idx(h.it.id);
                    if (i >= 0) S.items.splice(i, 1);
                    emit({ o: 'del', ids: [h.it.id] });
                } else {
                    h.its.slice().sort(function (a, b) { return a.at - b.at; }).forEach(function (e) {
                        e.it.done = now(); e.it.life = 0;   // вернувшаяся пометка не догорает за секунду
                        S.items.splice(Math.min(e.at, S.items.length), 0, e.it);
                        emit({ o: 'add', it: C.wireItem(e.it) });
                    });
                }
                changed();
                return true;
            },
            redoOne: function () {
                var h = S.redo.pop();
                if (!h) return false;
                S.hist.push(h);
                if (h.t === 'add') {
                    h.it.done = now();
                    S.items.push(h.it);
                    emit({ o: 'add', it: C.wireItem(h.it) });
                } else {
                    var ids = h.its.map(function (e) { return e.it.id; });
                    S.items = S.items.filter(function (it) { return ids.indexOf(it.id) < 0; });
                    emit(h.t === 'clr' ? { o: 'clr' } : { o: 'del', ids: ids });
                }
                changed();
                return true;
            },
            canUndo: function () { return S.hist.length > 0; },
            canRedo: function () { return S.redo.length > 0; },
            count: function () { return S.items.filter(function (it) { return it.k !== 'laser'; }).length; },

            // ── указатель (точка указки, прожектор) ─────────────────────
            pointer: function (x, y) {
                if (x === null || x === undefined) {
                    if (S.ptr) { S.ptr = null; emit({ o: 'ptr' }); changed(); }
                    return;
                }
                var k = S.tool === 'spot' ? 'spot' : 'laser';
                S.ptr = { x: x, y: y, k: k, c: S.color, r: S.spotR };
                emit({ o: 'ptr', x: x, y: y, k: k, c: S.color, r: S.spotR });
                changed();
            },

            // ── стоп-кадр ────────────────────────────────────────────────
            // Замораживает то, что сейчас уходит в кадр; запись и звук идут.
            freeze: function () {
                if (S.frozen) return true;
                S.stats.freezes++;
                tel('annot_freeze', { on: 1 });
                // Основа композитора уже несёт пометки — её следующий кадр
                // возьмём ДО пометок (paintOver), а не этот.
                if (S.lastBase && !S.baseHasInk && takeFreeze(S.lastBase)) return true;
                S.freezePending = true;   // возьмём следующий кадр
                changed();
                return true;
            },
            // ── лупа ─────────────────────────────────────────────────────
            // rect — доли основы (w === h), null — весь кадр. Нарисованное на
            // прежнем виде гаснет: оно указывало в другое место картинки.
            setZoom: function (rect, why) {
                var t = now();
                var to = C.cleanRect(rect);
                var cur = S.zoom ? S.zoom.to : null;
                if (!to && !cur) return;
                S.zoom = C.zoomTo(S.zoom, to, t);
                fadeAll('zoom');
                S.scene.ink = null;
                S.stats.zooms = (S.stats.zooms || 0) + (to ? 1 : 0);
                emit({ o: 'zoom', r: to });
                tel('annot_zoom', { on: to ? 1 : 0, depth: to ? Math.round(1 / to.w * 10) / 10 : 1, why: why || '' });
                changed();
            },
            zoomRect: function () { return C.zoomNow(S.zoom, now()); },
            zoomed: function () { return !!(S.zoom && S.zoom.to); },
            // Картинка снова живая; нарисованное на стоп-кадре уходит вместе с ним.
            thaw: function () {
                if (!S.frozen && !S.freezePending) return;
                S.frozen = null; S.freezePending = false;
                S.scene.ink = null;
                if (S.zoom && S.zoom.to) { S.zoom = C.zoomTo(S.zoom, null, now()); emit({ o: 'zoom', r: null }); }
                fadeAll('thaw');
                tel('annot_freeze', { on: 0 });
                changed();
            },
            frozen: function () { return !!(S.frozen || S.freezePending); },
            frozenCanvas: function () { return S.frozen; },

            // ── кадр с пометками картинкой ───────────────────────────────
            // PNG того, что сейчас в кадре (стоп-кадр или живой) с пометками.
            snapshot: function () {
                var base = S.frozen || S.lastBase;
                var dm = srcSize(base);
                if (!dm) return null;
                var c = mk(dm.w, dm.h), ctx = c.getContext('2d');
                try { C.drawZoomed(ctx, base, dm.w, dm.h, S.baseHasInk && !S.frozen ? null : C.zoomNow(S.zoom, now()), 0, 0, c.width, c.height); } catch (_) { return null; }
                if (!S.baseHasInk || S.frozen) C.render(ctx, { x: 0, y: 0, w: c.width, h: c.height }, S.items, now(), { laserIdleAt: S.laserIdleAt, scratch: scratch });
                S.stats.saves++;
                tel('annot_save_frame', { w: c.width, h: c.height });
                return c;
            },

            // ── превью ───────────────────────────────────────────────────
            // Последний кадр держится всегда (стоп-кадр и второе место
            // рисования — окно поверх программ — берут его отсюда).
            setPreview: function (b) { S.wantPreview = !!b; },
            previewSource: function () { return S.frozen || S.lastBase; },
            baseHasInk: function () { return S.baseHasInk && !S.frozen; },
            items: function () { return S.items; },
            renderInto: function (ctx, R, opts) {
                opts = opts || {};
                var t = now();
                C.render(ctx, R, S.items, t, { laserIdleAt: S.laserIdleAt, ptr: S.ptr, scratch: opts.mosaic ? scratch : null, remote: C.remoteList(S, t) });
            },
            // Указка зрителя (/screen-share): операция {o:'vptr', id, x, y, c, n}.
            remote: function (op) { if (C.applyOp(S, op, now())) changed(); },
            hasRemote: function () { return C.remoteList(S, now()).length > 0; },
            // Основа, которой нет в конвейере кадров: <video> превью ведущего
            // /screen-share. Из неё берутся стоп-кадр, «Сохранить кадр» и
            // взгляд «сменился ли слайд».
            noteSource: function (src) { noteBase(src, false); },
            drawing: function () { return S.drawing; },
            // → функция отписки (поверхность в закрытом окне поверх программ не остаётся слушать)
            onChange: function (fn) {
                if (typeof fn !== 'function') return function () {};
                S.listeners.push(fn);
                return function () { var i = S.listeners.indexOf(fn); if (i >= 0) S.listeners.splice(i, 1); };
            },
            setOnOp: function (fn) { S.onOp = typeof fn === 'function' ? fn : null; },
            // Всё состояние для подключившегося позже зрителя.
            snapshotOps: function () {
                var ops = [{ o: 'all', items: S.items.filter(function (it) { return it.k !== 'laser'; }).map(C.wireItem) }];
                if (S.ptr) ops.push({ o: 'ptr', x: S.ptr.x, y: S.ptr.y, k: S.ptr.k, c: S.ptr.c, r: S.ptr.r });
                if (S.zoom && S.zoom.to) ops.push({ o: 'zoom', r: S.zoom.to });
                return ops;
            },

            // ── фильтр кадров движка записи (recorder.js, лейн WebCodecs) ──
            recorderFilter: {
                // Кадр захвата → он же (рисовать нечего) или новый кадр с
                // пометками той же метки времени. `repaint` — повтор
                // последнего кадра на тике: основой его не считаем.
                apply: function (frame, o) {
                    if (!o || !o.repaint) noteBase(frame, true);
                    var t = now();
                    if (!active(t)) { S.dirty = false; return frame; }
                    var dm = srcSize(frame);
                    if (!dm) return frame;
                    var cv = api._frameCanvas;
                    if (!cv || cv.width !== dm.w || cv.height !== dm.h) cv = api._frameCanvas = mk(dm.w, dm.h);
                    var ctx = cv.getContext('2d', { alpha: false });
                    try {
                        var src = S.frozen || frame, sd = srcSize(src) || dm;
                        C.drawZoomed(ctx, src, sd.w, sd.h, C.zoomNow(S.zoom, t), 0, 0, dm.w, dm.h);
                        C.render(ctx, { x: 0, y: 0, w: dm.w, h: dm.h }, S.items, t, { laserIdleAt: S.laserIdleAt, ptr: S.ptr, scratch: scratch });
                        var out = new VideoFrame(cv, { timestamp: frame.timestamp, alpha: 'discard' });
                        S.dirty = false;
                        S.stats.frames++;
                        return out;
                    } catch (e) {
                        // Кадр без пометок лучше потерянного кадра.
                        return frame;
                    }
                },
                // Пометки меняются, а захват молчит (неподвижный экран):
                // движку нужно перерисовать последний кадр.
                wantsTick: function () {
                    var t = now();
                    return S.dirty || !!S.drawing || animating(t);
                },
                // Пометки есть — кадры несут холст.
                active: function () { return active(); },
                // Один слот: каждый дубль строит свой конвейер, и прежний
                // будильник должен уйти вместе с ним, а не копиться.
                onWake: function (fn) { S.wake = typeof fn === 'function' ? fn : null; }
            },

            // ── композитор (лейн MediaRecorder): поверх уже нарисованного ──
            paintOver: function (ctx, W, H) {
                var cv = ctx.canvas;
                if (S.freezePending) takeFreeze(cv);
                if (S.frozen) { try { ctx.drawImage(S.frozen, 0, 0, W, H); } catch (_) {} }
                var z = C.zoomNow(S.zoom, now());
                if (z) {
                    // лупа на композите: копия кадра и её участок обратно на весь кадр
                    var zc = S.zoomScratch;
                    if (!zc || zc.width !== W || zc.height !== H) zc = S.zoomScratch = mk(W, H);
                    try { zc.getContext('2d').drawImage(cv, 0, 0); C.drawZoomed(ctx, zc, W, H, z, 0, 0, W, H); } catch (_) {}
                }
                C.render(ctx, { x: 0, y: 0, w: W, h: H }, S.items, now(), { laserIdleAt: S.laserIdleAt, ptr: S.ptr, scratch: scratch });
                S.baseHasInk = true;
                dropBase(); S.lastBase = cv; S.lastBaseOwned = false;
                sceneSample(cv);
                S.dirty = false;
            },
            composeWants: function () { return S.dirty || !!S.drawing || animating(now()) || !!C.zoomNow(S.zoom, now()); },

            // ── конец дубля / сеанса ─────────────────────────────────────
            reset: function () {
                S.items.length = 0; S.hist.length = 0; S.redo.length = 0;
                S.frozen = null; S.freezePending = false; S.drawing = null; S.ptr = null;
                S.scene.ink = null; S.scene.cur = null; S.scene.prev = null; S.baseHasInk = false;
                S.zoom = null;
                dropBase();
                emit({ o: 'clr' }); emit({ o: 'ptr' }); emit({ o: 'zoom', r: null });
                changed();
                // Сброс — не изменение кадра: движку перерисовывать нечего
                // (иначе каждый дубль начинался с лишнего тика).
                S.dirty = false;
            },
            stats: function () {
                return { items: S.stats.items, tools: Object.keys(S.stats.tools).join(','), freezes: S.stats.freezes, undo: S.stats.undo,
                    autoClear: S.stats.autoClear, saves: S.stats.saves, recog: S.stats.recog, frames: S.stats.frames, zooms: S.stats.zooms || 0 };
            },
            resetStats: function () { S.stats = { items: 0, tools: {}, freezes: 0, undo: 0, autoClear: 0, saves: 0, recog: 0, frames: 0 }; },
            tel: tel,
            _state: function () { return S; }
        };
        return api;
    }

    window.ScreenAnnotate = { create: create, COLORS: COLORS };
})();
